import { toast } from 'react-toastify';
import HttpClient from 'services/HttpClient';
import { UploadFileFn } from './typings';
import {
  isVideoFile,
  needsCompression,
  validateVideoForUpload,
  getVideoFileInfo,
} from './video-compression';

// Import constant for maximum video size validation
import {
  isImageFile,
  needsCompression as needsImageCompression,
  validateImageForUpload,
  compressImage
} from './image-compression';
import localization from 'localization';
const MAX_VIDEO_SIZE = 10 * 1024 * 1024 * 1024; // 10,000 MB (10 GB)
const MAX_IMAGE_SIZE = 100 * 1024 * 1024; // 100 MB

export const uploadFile: UploadFileFn = (
  { file, id, appletId },
  onProgress,
) => {
  return new Promise(async (resolve, reject) => {
    let fileSize: number | undefined;
    try {
      if (isVideoFile(file)) {
        const fileInfo = await getVideoFileInfo(file);
        fileSize = fileInfo?.size;
      } else if (isImageFile(file)) {
        const { getImageFileInfo } = await import('./image-compression');
        const fileInfo = await getImageFileInfo(file);
        fileSize = fileInfo?.size;
        console.log('fileSize', fileSize);
      }
    } catch (err) {
      // Silently continue if file size cannot be obtained
    }

    try {
      if (isVideoFile(file)) {
        const validation = await validateVideoForUpload(file);

        if (!validation.valid) {
          reject(new Error(validation.error || 'The video is not valid for upload'));
          return;
        }

        const needsComp = await needsCompression(file);
        await getVideoFileInfo(file);
        if (needsComp) {
          onProgress(5);
        } else {
          onProgress(5);
        }
      }

      let fileToUpload = file;
      let imageNeedsCompression = false;
      if (isImageFile(file)) {
        const validation = await validateImageForUpload(file);

        if (!validation.valid) {
          reject(new Error(validation.error || 'The image is not valid for upload'));
          return;
        }

        imageNeedsCompression = await needsImageCompression(file);

        if (imageNeedsCompression) {
          onProgress(5);
          try {
            fileToUpload = await compressImage(file, {}, (progress) => {

              const compressionProgress = 5 + Math.round((progress / 100) * 25);
              onProgress(compressionProgress);
            });
            onProgress(30);
          } catch (error) {
            fileToUpload = file;
            imageNeedsCompression = false;
          }
        } else {
          onProgress(5);
        }
      }

      // Check internet connection before attempting upload
      if (!navigator.onLine) {
        reject(new Error('NETWORK_ERROR: No internet connection. Please check your connection and try again.'));
        return;
      }


      onProgress(6);
      let linkResponse;
      try {
        linkResponse = await HttpClient.post('/storage', {
          originalName: fileToUpload.uri.split('/').pop(),
          mimeType: fileToUpload.mime || fileToUpload.type,
        });
        onProgress(8);
      } catch (err: any) {

        if (!navigator.onLine || err.code === 'NETWORK_ERROR' || err.message?.includes('Network Error') || err.message?.includes('Failed to fetch')) {
          reject(new Error('NETWORK_ERROR: Unable to connect to server. Please check your internet connection and try again.'));
          return;
        }
        throw err;
      }
      const { link } = linkResponse.data;

      const xhr = new XMLHttpRequest();
      xhr.open('PUT', link);
      xhr.setRequestHeader('Content-Type', fileToUpload.mime || fileToUpload.type || 'video/mp4');

      // Timer to show warning if upload takes longer than 5 seconds
      let warningTimeout: NodeJS.Timeout | null = null;
      let warningShown = false;

      // Variable to store blob size (available in xhr.onload)
      let blobSize: number | undefined = undefined;

      xhr.upload.onprogress = (ev) => {
        if (ev.total) {
          // Upload occupies 20-100% of total progress (20% already used for validation/blob retrieval)
          // Or 5-100% if there was no image compression
          const baseProgress = imageNeedsCompression ? 30 : 20;
          const uploadProgress = baseProgress + Math.round((ev.loaded / ev.total) * (100 - baseProgress));

          onProgress(uploadProgress);
        }
      };

      xhr.onload = async () => {
        // Clear timer if upload finished
        if (warningTimeout) {
          clearTimeout(warningTimeout);
          warningTimeout = null;
        }

        const fileUrl = link.split('?')[0];

        // If it's a video, call optimization endpoint (backend will decide if compression is needed)
        if (isVideoFile(fileToUpload)) {
          // Try to get video information, but continue even if it fails
          let fileInfo: { size: number } | null = null;
          try {
            fileInfo = await getVideoFileInfo(fileToUpload);
          } catch (infoErr: any) {
            // Use saved blob size if available
            if (blobSize) {
              fileInfo = { size: blobSize };
            }
          }

          // Extract s3Key from URL
          // URL format: https://bucket.s3.region.amazonaws.com/uploads/user-xxx/filename.ext
          try {
            const urlObj = new URL(fileUrl);
            const s3Key = urlObj.pathname.substring(1); // Remove initial '/'

            // If we don't have fileInfo but have blobSize, use saved size
            const fileSizeToSend = fileInfo?.size || blobSize || 0;

            if (fileSizeToSend > 0) {
              // Call optimization endpoint asynchronously (don't wait)
              // Backend will decide if compression is needed based on size
              HttpClient.post('/storage/optimize', {
                s3Key,
                fileSize: fileSizeToSend,
                contentType: fileToUpload.mime || fileToUpload.type,
              }).catch(() => {
                // Don't reject upload if optimization fails
              });
            }
          } catch (err: any) {
            // Silently continue if optimization request fails
          }
        }

        resolve({ fileUrl, id, appletId });
      };

      xhr.onerror = (ev) => {
        // Clear timer if there's an error
        if (warningTimeout) {
          clearTimeout(warningTimeout);
          warningTimeout = null;
        }

        // Check if it's a network error
        if (!navigator.onLine) {
          reject(new Error('NETWORK_ERROR: No internet connection. Please check your connection and try again.'));
        } else {
          reject(new Error('NETWORK_ERROR: Unable to upload file. Please check your internet connection and try again.'));
        }
      };

      xhr.ontimeout = () => {
        // Clear timer if there's a timeout
        if (warningTimeout) {
          clearTimeout(warningTimeout);
          warningTimeout = null;
        }

        reject(new Error('NETWORK_ERROR: Upload timeout. Please check your internet connection and try again.'));
      };

      let blob;
      try {
        onProgress(10); // Starting blob retrieval

        // Preferred strategy: If we have the original File, use it directly
        if (fileToUpload.originalFile) {
          blob = fileToUpload.originalFile;
        } else {
          // If we don't have the original File, try to read from URI
          let blobObtained = false;

          // Strategy 1: Try using fetch first (fast for small/medium files)
          if (!blobObtained) {
            try {
              const fetchController = new AbortController();
              const fetchTimeout = setTimeout(() => fetchController.abort(), 30000); // 30 seconds timeout

              blob = await fetch(fileToUpload.uri, { signal: fetchController.signal }).then((r) => r.blob());
              clearTimeout(fetchTimeout);
              blobObtained = true;
            } catch (fetchErr: any) {
              // Continue with next strategy
            }
          }

          // Strategy 2: If fetch fails, use XMLHttpRequest with longer timeout
          if (!blobObtained) {
            try {
              onProgress(12); // Switching to alternative method
              blob = await new Promise<Blob>((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open('GET', fileToUpload.uri, true);
                xhr.responseType = 'blob';

                // 10 minute timeout for very large files
                xhr.timeout = 10 * 60 * 1000;

                let timeoutId: NodeJS.Timeout | null = null;

                xhr.onload = () => {
                  if (timeoutId) clearTimeout(timeoutId);
                  if (xhr.status === 200) {
                    resolve(xhr.response);
                  } else {
                    reject(new Error(`Failed to load file: ${xhr.status} ${xhr.statusText}`));
                  }
                };

                xhr.onerror = () => {
                  if (timeoutId) clearTimeout(timeoutId);
                  reject(new Error('Failed to load file: Network error'));
                };

                xhr.ontimeout = () => {
                  if (timeoutId) clearTimeout(timeoutId);
                  reject(new Error('Failed to load file: Timeout'));
                };

                // Additional safety timeout
                timeoutId = setTimeout(() => {
                  xhr.abort();
                  reject(new Error('Failed to load file: Timeout (manual)'));
                }, 10 * 60 * 1000);

                xhr.send();
              });
              blobObtained = true;
            } catch (xhrErr: any) {
              // Continue with next strategy
            }
          }

          if (!blobObtained) {
            throw new Error('Unable to read file from URI. The file may be too large. Please try selecting the file again.');
          }
        }

        // Verify that blob is defined
        if (!blob) {
          throw new Error('Unable to read file. All read strategies failed.');
        }

        // Save blob size to use later in xhr.onload
        blobSize = blob.size;
        onProgress(15); // Blob obtained, preparing to upload

        // Validate obtained blob size (final validation before upload)
        if (isVideoFile(fileToUpload)) {
          if (blob.size > MAX_VIDEO_SIZE) {
            reject(new Error('Your file exceeds the size limit (100 mb for images, 10,000 mb for video). Please try again'));
            return;
          }
        } else if (isImageFile(fileToUpload)) {
          if (blob.size > MAX_IMAGE_SIZE) {
            reject(new Error('Your file exceeds the size limit (100 mb for images, 10,000 mb for video). Please try again'));
            return;
          }
        }
      } catch (blobErr: any) {
        if (!navigator.onLine || blobErr.message?.includes('Failed to fetch') || blobErr.message?.includes('Network error')) {
          reject(new Error('NETWORK_ERROR: Unable to read file. Please check your internet connection and try again.'));
          return;
        }
        throw blobErr;
      }

      // Start 5 second timer when blob is sent
      onProgress(20); // Starting upload to S3
      warningTimeout = setTimeout(() => {
        if (!warningShown) {
          warningShown = true;
          toast.info(localization.fileUploadLargeFileWarning, {
            position: 'top-center',
            autoClose: 5000,
          });
        }
      }, 5000);

      xhr.send(blob);
    } catch (err: any) {
      // Check if it's already a network error we created
      if (err.message?.startsWith('NETWORK_ERROR:')) {
        reject(err);
      } else if (!navigator.onLine || err.message?.includes('Network Error') || err.message?.includes('Failed to fetch')) {
        reject(new Error('NETWORK_ERROR: Unable to upload file. Please check your internet connection and try again.'));
      } else {
        reject(err);
      }
    }
  });
};
